import { api } from "@/api/http";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useEffect, useMemo, useState } from "react";
import { X, Loader2, Package } from "lucide-react";
import { formatBRLFromNumericString, parseBRLToNumericString, normalizeDecimalInput } from "@/utils/money";

type LookupPeca = {
  id: string;
  codigo?: string | null;
  descricao: string;
  marca?: string | null;
  precoVenda?: any;
  estoqueAtual?: any;
  estoqueMin?: any;
};

type Props = {
  open: boolean;
  osId: string;
  onClose: () => void;
  onCreated: () => void; // recarrega OS
};

function onlyNumbers(s: string) {
  return s.replace(/[^\d.,-]/g, "");
}
function isValidDecimal(s: string) {
  if (!s) return true;
  return /^-?\d+(\.\d+)?$/.test(s);
}

export default function AddPecaQuickModal({ open, osId, onClose, onCreated }: Props) {
  const [codigo, setCodigo] = useState("");
  const [qtd, setQtd] = useState("1");
  const [valorUnit, setValorUnit] = useState("");

  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<LookupPeca[]>([]);
  const [pecaId, setPecaId] = useState<string>("");

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCodigo("");
    setQtd("1");
    setValorUnit("");
    setQ("");
    setResults([]);
    setPecaId("");
    setSaving(false);
  }, [open]);

  const qtdN = useMemo(() => normalizeDecimalInput(qtd), [qtd]);

  const disabled = useMemo(() => {
    if (saving) return true;
    if (!qtdN || !isValidDecimal(qtdN)) return true;

    const vN = parseBRLToNumericString(valorUnit);
    if (vN && !isValidDecimal(vN)) return true;

    // precisa ter pecaId OU codigo
    if (!pecaId && !codigo.trim()) return true;
    return false;
  }, [saving, qtdN, valorUnit, pecaId, codigo]);

  async function search(query: string) {
    const qq = query.trim();
    if (!qq) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/api/pecas/lookup", { params: { q: qq, limit: 10 } });
      setResults(data?.data ?? []);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Erro ao buscar peças");
    } finally {
      setLoading(false);
    }
  }

  async function onSubmit() {
    if (disabled) return;

    const payload: any = {
      qtd: qtdN ? qtdN : "1",
    };

    const vN = parseBRLToNumericString(valorUnit);
    if (vN) payload.valorUnit = vN;

    if (pecaId) payload.pecaId = pecaId;
    else payload.codigo = codigo.trim();

    setSaving(true);
    try {
      await api.post(`/api/os/${osId}/itens/peca`, payload);
      toast.success("Peça adicionada e estoque baixado ✅");
      onCreated();
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.error || "Erro ao adicionar peça";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/40" onClick={saving ? undefined : onClose} />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-2xl rounded-xl bg-white shadow-lg border border-slate-200">
          <div className="flex items-start justify-between gap-3 p-4 border-b border-slate-200">
            <div>
              <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                <Package size={18} />
                Atalho: adicionar peça
              </h3>
              <p className="text-sm text-slate-600 mt-1">Informe um código direto ou busque e selecione.</p>
            </div>

            <button onClick={saving ? undefined : onClose} className="p-1 rounded hover:bg-slate-100" aria-label="Fechar">
              <X size={18} />
            </button>
          </div>

          <div className="p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700">Código (opcional)</label>
                <Input
                  value={codigo}
                  onChange={(e) => {
                    setCodigo(e.target.value);
                    if (e.target.value.trim()) setPecaId("");
                  }}
                  placeholder="Ex: P-0001"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700">Quantidade</label>
                <Input value={qtd} onChange={(e) => setQtd(onlyNumbers(e.target.value))} placeholder="1" inputMode="decimal" />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700">Valor unit (opcional)</label>
                <Input
                  value={valorUnit}
                  onChange={(e) => setValorUnit(e.target.value)}
                  onFocus={() => {
                    const raw = parseBRLToNumericString(valorUnit);
                    setValorUnit(raw ? raw.replace(".", ",") : "");
                  }}
                  onBlur={() => {
                    const raw = parseBRLToNumericString(valorUnit);
                    setValorUnit(raw ? formatBRLFromNumericString(raw) : "");
                  }}
                  placeholder="R$ 0,00"
                  inputMode="decimal"
                />
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 p-3 bg-slate-50 text-sm text-slate-600">
              Dica: se você <b>selecionar uma peça abaixo</b>, não precisa preencher o código.
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Buscar peça (opcional)</label>
              <div className="flex gap-2">
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Buscar por código, descrição, marca..."
                />
                <Button type="button" variant="outline" disabled={loading || !q.trim()} onClick={() => search(q)}>
                  {loading ? <Loader2 className="animate-spin" size={16} /> : "Buscar"}
                </Button>
              </div>

              <div className="rounded-lg border border-slate-200 overflow-hidden">
                {results.length === 0 ? (
                  <div className="p-3 text-sm text-slate-500">Busque e selecione uma peça (opcional).</div>
                ) : (
                  <div className="divide-y max-h-64 overflow-auto">
                    {results.map((p) => {
                      const selected = pecaId === p.id;
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => {
                            setPecaId(p.id);
                            setCodigo("");
                          }}
                          className={`w-full text-left p-3 hover:bg-slate-50 transition ${selected ? "bg-slate-50" : ""}`}
                        >
                          <div className="font-medium text-slate-900">
                            {p.codigo ? `${p.codigo} • ` : ""}{p.descricao}
                          </div>
                          <div className="text-xs text-slate-500">
                            {p.marca ? `Marca: ${p.marca} • ` : ""}
                            Estoque: {String(p.estoqueAtual ?? "0")}
                            {p.precoVenda !== undefined ? ` • Venda: ${String(p.precoVenda)}` : ""}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={onSubmit} disabled={disabled} className="gap-2">
              {saving ? <Loader2 className="animate-spin" size={16} /> : null}
              Adicionar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
